import _ from 'lodash'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/vanilla/shallow'

import { PRODUCTION_ENV, SESSION_STORE_NAME, STORE_NAME } from '~/constants'
import createAgentSlice, { TAgentState } from '~/features/agents/store'
import createCampaignsSlice, {
  TCampaignsState,
} from '~/features/campaigns/store'
import createChatbotSlice, { TChatbotState } from '~/features/chatbot/store'
import createConversationSlice, {
  TConversationState,
} from '~/features/conversations/store'
import createMarketingSlice, {
  TMarketingState,
} from '~/features/marketing/store'
import createOrganizationSlice, {
  TOrganizationState,
} from '~/features/organizations/store'
import createSettingsSlice, { TSettingsState } from '~/features/settings/store'
import createUIElementsSessionSlice, {
  TUIElementsSessionState,
} from '~/features/ui/sessionStore'
import createUIElementsSlice, { TUIElementsState } from '~/features/ui/store'
import createUserSlice, { TUserState } from '~/features/user/store'
import EEnvironmentVariables from '~/models/config'
import EStoreStateKeys from '~/models/store'
import AuthService from '~/services/auth'
import { sessionStoreInitialValues, storeInitialValues } from '~/store/utils'

type StoreType = TUserState &
  TSettingsState &
  TChatbotState &
  TConversationState &
  TOrganizationState &
  TUIElementsState &
  TCampaignsState &
  TMarketingState & {
    resetStore: (pathsToPreserve?: string[]) => void
  } & {
    [key: string]: unknown
  }

export const store = createWithEqualityFn<StoreType>()(
  devtools(
    persist(
      (set, ...a) => {
        return {
          ...createUserSlice(set, ...a),
          ...createSettingsSlice(set, ...a),
          ...createChatbotSlice(set, ...a),
          ...createConversationSlice(set, ...a),
          ...createOrganizationSlice(set, ...a),
          ...createUIElementsSlice(set, ...a),
          ...createCampaignsSlice(set, ...a),
          ...createMarketingSlice(set, ...a),
          resetStore: (pathsToPreserve?: string[]) => {
            const initialState = _.cloneDeep(storeInitialValues())
            const getState = a[0]
            const currentState = getState()

            // If pathsToPreserve is provided, merge preserved paths into cloned initial state
            if (pathsToPreserve) {
              pathsToPreserve.forEach((path) => {
                const preservedValue = _.get(currentState, path)
                if (preservedValue !== undefined) {
                  _.set(initialState, path, preservedValue)
                }
              })
            }

            return set(initialState)
          },
        }
      },
      {
        name: STORE_NAME,
        version: 6,
        migrate: async () => {
          await AuthService.signOut()
          localStorage.clear()
          return storeInitialValues()
        },
        partialize: (state) => {
          const keysToDismissFromStore = [EStoreStateKeys.chatbot]
          return Object.fromEntries(
            Object.entries(state).filter(
              ([key]) =>
                !keysToDismissFromStore.includes(key as EStoreStateKeys),
            ),
          )
        },
      },
    ),
    {
      enabled:
        import.meta.env[EEnvironmentVariables.VITE_ENV] !== PRODUCTION_ENV,
      name: STORE_NAME,
    },
  ),
  shallow,
)

type SessionStoreType = TAgentState &
  TUIElementsSessionState & {
    resetStore: () => void
  } & {
    [key: string]: unknown
  }

export const sessionStore = createWithEqualityFn<SessionStoreType>()(
  devtools(
    persist(
      (set, ...a) => ({
        ...createAgentSlice(set, ...a),
        ...createUIElementsSessionSlice(set, ...a),
        resetStore: () => {
          return set(_.cloneDeep(sessionStoreInitialValues()))
        },
      }),
      {
        name: SESSION_STORE_NAME,
        version: 2,
        migrate: async (state) => {
          return _.merge(state, sessionStoreInitialValues()) as SessionStoreType
        },
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
    {
      enabled:
        import.meta.env[EEnvironmentVariables.VITE_ENV] !== PRODUCTION_ENV,
      name: SESSION_STORE_NAME,
    },
  ),
  shallow,
)
